import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Guidance overlay: finds a target element by `data-testid` inside
 * `containerRef` and draws an animated ring around it plus a small tooltip.
 * Recomputes on resize/scroll so it tracks the target through layout shifts
 * (e.g. opening the mobile drawer). Exactly one spotlight is ever rendered
 * per step: callers pass a single `targetTestId`.
 *
 * The tooltip is horizontally clamped to the container's bounds (see
 * tooltipOffset below): centering it under the target unconditionally
 * caused real overflow in the narrow Phone-mode iframe whenever the
 * spotlighted element sat near the edge (e.g. the mobile hamburger).
 */
export default function Spotlight({ containerRef, targetTestId, label, reducedMotion = false }) {
  const [rect, setRect] = useState(null);
  const [tooltipOffset, setTooltipOffset] = useState(0);
  const frameRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!targetTestId) { setRect(null); return; }
    const root = containerRef?.current;
    if (!root) return;

    function measure() {
      const el = root.querySelector(`[data-testid="${targetTestId}"]`);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      const containerRect = root.getBoundingClientRect();
      setRect({
        top: r.top - containerRect.top,
        left: r.left - containerRect.left,
        width: r.width,
        height: r.height,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
      });
    }

    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(root);
    const onScroll = () => { cancelAnimationFrame(frameRef.current); frameRef.current = requestAnimationFrame(measure); };
    root.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    const interval = setInterval(measure, 400); // catches content that reflows without a resize/scroll event

    return () => {
      ro?.disconnect();
      root.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      clearInterval(interval);
      cancelAnimationFrame(frameRef.current);
    };
  }, [containerRef, targetTestId]);

  useLayoutEffect(() => {
    if (!rect || !label || !tooltipRef.current) { setTooltipOffset(0); return; }
    const tooltipWidth = tooltipRef.current.offsetWidth;
    const boxCenterX = rect.left - 4 + (rect.width + 8) / 2; // center of the ring box, container-relative
    const margin = 6;
    const minLeft = margin;
    const maxLeft = Math.max(margin, rect.containerWidth - margin - tooltipWidth);
    const idealLeft = boxCenterX - tooltipWidth / 2;
    const clampedLeft = Math.min(Math.max(idealLeft, minLeft), maxLeft);
    setTooltipOffset(clampedLeft - boxCenterX); // offset from the default centered position
  }, [rect, label]);

  if (!rect) return null;

  // If the target sits outside the visible container (below/above the
  // fold), the ring alone would be invisible or clamped to nothing: show a
  // small floating "Scroll down/up" chip instead of leaving the visitor to
  // guess where the highlighted control went.
  const targetBottom = rect.top + rect.height;
  const scrollDirection = rect.top >= rect.containerHeight ? "down" : targetBottom <= 0 ? "up" : null;

  const pad = 4;
  // Clamp the ring box itself to the container's bounds: a target sitting
  // flush against the edge (e.g. a full-width button) would otherwise push
  // the padded ring a few px past the container and cause real overflow.
  const rawLeft = rect.left - pad;
  const rawTop = rect.top - pad;
  const rawWidth = rect.width + pad * 2;
  const rawHeight = rect.height + pad * 2;
  const left = Math.max(0, rawLeft);
  const top = Math.max(0, rawTop);
  const width = Math.min(rawWidth - (left - rawLeft), rect.containerWidth - left);
  const height = Math.min(rawHeight - (top - rawTop), rect.containerHeight - top);

  return (
    <div
      className="absolute pointer-events-none z-30"
      style={{ top, left, width, height }}
      data-testid="walkthrough-spotlight"
      data-spotlight-target={targetTestId}
    >
      <div
        className={`w-full h-full rounded-lg border-4 border-rose-500 ${reducedMotion ? "" : "animate-pulse motion-reduce:animate-none"}`}
        style={{ boxShadow: "0 0 0 9999px rgba(15,23,42,0.35)" }}
      />
      {scrollDirection && (
        <div
          className={`absolute left-1/2 -translate-x-1/2 ${scrollDirection === "down" ? "-top-4 translate-y-full" : "-bottom-4 -translate-y-full"} whitespace-nowrap rounded-full bg-rose-600 text-white text-[11px] font-semibold px-3 py-1.5 shadow-lg animate-bounce motion-reduce:animate-none`}
          style={{
            position: "fixed",
            [scrollDirection === "down" ? "bottom" : "top"]: 12,
            left: "50%",
            transform: "translateX(-50%)",
          }}
          data-testid="walkthrough-spotlight-scroll-hint"
        >
          {scrollDirection === "down" ? "Scroll down ↓" : "Scroll up ↑"}
        </div>
      )}
      {label && (
        <div
          ref={tooltipRef}
          className="absolute -bottom-9 left-1/2 whitespace-nowrap rounded-lg bg-slate-900 text-white text-[11px] font-medium px-2.5 py-1.5 shadow-lg"
          style={{ transform: `translateX(calc(-50% + ${tooltipOffset}px))` }}
          data-testid="walkthrough-spotlight-tooltip"
        >
          {label}
          <div
            className="absolute -top-1 w-2 h-2 bg-slate-900 rotate-45"
            style={{ left: "50%", transform: `translateX(calc(-50% - ${tooltipOffset}px))` }}
          />
        </div>
      )}
    </div>
  );
}
